import React from 'react';
import {
    createGet,
    createMutation,
    mutateCache,
    simpleGet,
    useGet,
} from '../hooks/useApi';
import {
    NotificationModel,
    NotificationsModel,
} from '../models/NotificationModel';
import { HttpClient } from '../utils/HttpUilts';
import { safeUrl } from '../utils/safeUrl';
import { useAuth } from '../contexts/AuthContext';

export type NotificationRequest = Omit<
    NotificationModel,
    'notificationId' | 'createdAt' | 'updatedAt'
> & {
    notificationId?: string;
};

export const notificationsAPI = {
    admin: {
        get: simpleGet<NotificationsModel>`/admin/notifications/`,
        getSingle: (id: string) =>
            simpleGet<NotificationModel>`/admin/notification/${id}`,
        delete: (id: string) => HttpClient.del(`/admin/notification/${id}`),
        mutate: createMutation<
            NotificationRequest,
            {
                status: string | number;
                message?: string;
                data?: NotificationModel;
            }
        >(async ({ authenticated }, data, opts) =>
            data.notificationId
                ? authenticated.put(
                      `/admin/notification/${data.notificationId}`,
                      data,
                      opts
                  )
                : authenticated.post('/admin/notification/', data, opts)
        ),
    },
    private: {
        get: () =>
            createGet<{ data: NotificationsModel }>(
                safeUrl`/notifications`,
                null,
                {
                    onFresh(_key, notifications) {
                        for (const notification of notifications.data) {
                            mutateCache(
                                notificationsAPI.private.getSingle(
                                    notification.notificationId
                                ),
                                {
                                    data: notification,
                                }
                            );
                        }
                    },
                }
            ),
        getSingle: (id: string) =>
            simpleGet<NotificationModel>`/notification/${id}`,
    },
    public: {
        get: () => {
            return createGet<{ data: NotificationsModel }>(
                safeUrl`/public/notifications/`,
                null,
                {
                    onFresh(_key, notifications) {
                        for (const notification of notifications.data) {
                            mutateCache(
                                notificationsAPI.public.getSingle(
                                    notification.notificationId
                                ),
                                {
                                    data: notification,
                                }
                            );
                        }
                    },
                }
            );
        },
        getSingle: (id: string) =>
            simpleGet<NotificationModel>`/public/notification/${id}`,
    },
};

export const useGetAllNotifications = () => {
    const { isAuthenticated } = useAuth();
    const { data: publicNotifications, isLoading: publicLoading } = useGet(
        notificationsAPI.public.get()
    );
    const { data: privateNotifications, isLoading: privateLoading } = useGet(
        isAuthenticated && notificationsAPI.private.get()
    );

    const notifications = React.useMemo<NotificationsModel>(() => {
        return [
            ...(publicNotifications?.data ?? []),
            ...(privateNotifications?.data ?? []),
        ];
    }, [publicNotifications, privateNotifications]);

    const isLoading = React.useMemo(() => {
        if (isAuthenticated) {
            return publicLoading || privateLoading;
        }
        return publicLoading;
    }, [publicLoading, privateLoading, isAuthenticated]);

    return { notifications, isLoading };
};
